import React from 'react';
import { Typography, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
	formatDateTo24HrTime,
	StatefulTimeSelect
} from '@components/StatefulInputs/StatefulTimeSelect/StatefulTimeSelect';
import { useCollectionEntity } from '@utilities/hooks/useCollectionEntity/useCollectionEntity';
import { Unit } from '@data/unit/Unit';
import { ENDPOINT_USER_UNITS } from '@utilities/apiConstants';
import {
	PartialReleaseEndTimeValidation,
	PartialReleaseStartTimeValidation
} from '@components/BlockRelease/BlockReleaseRequest/BlockReleaseRequestPage/BlockDetailsSection/PartialReleaseSection/PartialReleaseValidation';
import { useFormContext } from 'react-hook-form';
import {
	SelectedReleaseInfo
} from '@components/BlockRelease/BlockReleaseRequest/BlockReleaseRequestContext/BlockReleaseRequestContext.types';
import {
	useBlockReleaseRequestContext
} from '@components/BlockRelease/BlockReleaseRequest/BlockReleaseRequestContext/BlockReleaseRequestContext';
import {
	formatRoundAndParseReleaseDateTime
} from '@components/BlockRelease/utils/roundAndParseReleaseDateTime/roundAndParseReleaseDateTime';
import { BlockReleaseState } from '@data/blockReleases/types/BlockReleaseState';

const useStyles = makeStyles((theme) => ({
	root: {
		marginBottom: theme.spacing(3),
	},
	itemText: {
		margin: 0,
		marginBottom: theme.spacing(3),
		fontWeight: 600,
	},
	inputContainer: {
		'display': 'flex',
		'justifyContent': 'space-between',
		'gap': theme.spacing(3),
		'& > div': {
			flex: 1,
		}
	},
}));

interface PartialReleaseSectionProps {
	selectedReleaseInfo: SelectedReleaseInfo
}

export const PartialReleaseSection = ({ selectedReleaseInfo }: PartialReleaseSectionProps) => {
	const classes = useStyles();

	const { trigger } = useFormContext();
	const { blockInUse } = useBlockReleaseRequestContext();

	const hospitalTimeZone = selectedReleaseInfo?.facility?.hospitalTimeZone ?? '';
	const unitName = selectedReleaseInfo?.blockInfo?.blocks?.[0]?.unit || '';
	const { partialReleaseEnabled } = useCollectionEntity<Unit>(ENDPOINT_USER_UNITS, unitName, 'name');
	const { startDateTime, endDateTime, blockStartTime, blockEndTime } = selectedReleaseInfo.selectedRelease;

	const roundedStartDateTime = startDateTime && formatRoundAndParseReleaseDateTime(startDateTime);
	const roundedEndDateTime = endDateTime && formatRoundAndParseReleaseDateTime(endDateTime);
	const nudgeStart = formatDateTo24HrTime(hospitalTimeZone, roundedStartDateTime);
	const nudgeEnd = formatDateTo24HrTime(hospitalTimeZone, roundedEndDateTime);
	const isFlipRecommendation = selectedReleaseInfo.selectedRelease.state === BlockReleaseState.FLIP_RECOMMENDATION;

	if (!partialReleaseEnabled) {
		return null;
	}

	const optionsConfig = {
		start: blockStartTime,
		end: blockEndTime,
		increment: 15,
	};

	const triggerValidation = () => {
		void trigger();
	};

	return (
		<Box className={classes.root}>
			<Typography component="h3" className={classes.itemText}>
				Release unused block time?
			</Typography>
			<Box className={classes.inputContainer}>
				<StatefulTimeSelect
					label="Start time"
					dataField="blockReleaseRequest-partialRelease-startTime"
					controllerOptions={{
						defaultValue: nudgeStart,
						rules: {
							...PartialReleaseStartTimeValidation,
							onChange: triggerValidation,
						},
						disabled: blockInUse || isFlipRecommendation,
					}}
					name="startTime"
					optionsConfig={optionsConfig}
				/>
				<StatefulTimeSelect
					label="End time"
					dataField="blockReleaseRequest-partialRelease-endTime"
					controllerOptions={{
						defaultValue: nudgeEnd,
						rules: {
							...PartialReleaseEndTimeValidation,
							onChange: triggerValidation,
						},
						disabled: blockInUse || isFlipRecommendation,
					}}
					name="endTime"
					optionsConfig={optionsConfig}
				/>
			</Box>
		</Box>
	);
};
